// Vista previa de los correos de licencias 3CX: el MISMO HTML que arma la app en el servidor
// (xmartek-3cx-app/src/lib/licencias3cx.ts, bloque "correo en HTML"). Si se cambia alla, copiar aca.
export type Idioma = 'ES' | 'EN';
export type Licencia = { clave: string; vence: string; edicion: string; perpetua: boolean; llamadas: number | null; extensiones: number | null; tipo: string; resellerId: number | null };
export type LineaInterna = Licencia & { dias: number; partner: string; correo: string; contacto: string; precio: string };

const rellenar = (texto: string, datos: Record<string, string>) => texto.replace(/\{\{\s*([a-zA-Z]+(?:\.[a-zA-Z]+)?)\s*\}\}/g, (_, k: string) => datos[k] ?? '');
import { conEnlaces } from './correo';

const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const TH = 'style="background:#1f3a68;color:#ffffff;font-weight:600;padding:6px 10px;border:1px solid #1f3a68;text-align:center;font-size:13px"';
const TD = 'style="padding:5px 10px;border:1px solid #c9d1de;font-size:13px;text-align:center"';
const TDL = 'style="padding:5px 10px;border:1px solid #c9d1de;font-size:13px;font-family:Consolas,monospace"';
const tablaDe = (cab: string[], filas: string[][]) =>
  `<table cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:8px 0 14px;font-family:Arial,Helvetica,sans-serif">` +
  `<tr>${cab.map((c) => `<th ${TH}>${esc(c)}</th>`).join('')}</tr>` +
  filas.map((f) => `<tr>${f.map((c, i) => `<td ${i === 0 ? TDL : TD}>${c}</td>`).join('')}</tr>`).join('') +
  `</table>`;

// Texto del correo que recibe el cliente: igual al del servidor, en hora de Bogota (no la zona de Mi perfil).
const fechaCorta = (iso: string, idioma: Idioma) =>
  new Date(iso).toLocaleDateString(idioma === 'ES' ? 'es-CO' : 'en-US', { timeZone: 'America/Bogota', day: 'numeric', month: 'short', year: 'numeric' });
const colorDias = (d: number) => (d <= 15 ? '#c62828' : d <= 30 ? '#b26a00' : '#1b5e20');

export function tablaHtml(ls: (Licencia & { dias: number })[], idioma: Idioma) {
  const es = idioma === 'ES';
  return tablaDe(
    es ? ['Licencia', 'Edición', 'SC', 'Tipo', 'Vence', 'Días'] : ['License', 'Edition', 'SC', 'Type', 'Expires', 'Days'],
    [...ls]
      .sort((a, b) => a.dias - b.dias)
      .map((l) => [
        esc(l.clave),
        esc(l.edicion),
        l.llamadas ? `<b>${l.llamadas}</b>` : '—',
        l.perpetua ? (es ? 'Mantenimiento' : 'Maintenance') : es ? 'Anual' : 'Annual',
        esc(fechaCorta(l.vence, idioma)),
        `<b style="color:${colorDias(l.dias)}">${l.dias}</b>`,
      ]),
  );
}

export function tablaInternaHtml(ls: LineaInterna[]) {
  const porPartner = new Map<string, LineaInterna[]>();
  for (const l of [...ls].sort((a, b) => a.dias - b.dias)) porPartner.set(l.partner, [...(porPartner.get(l.partner) ?? []), l]);
  return [...porPartner.entries()]
    .map(([partner, g]) => {
      const cab = `<p style="margin:14px 0 2px;font-family:Arial,Helvetica,sans-serif"><b>${esc(partner)}</b>${g[0].contacto ? ` · ${esc(g[0].contacto)}` : ''}${g[0].correo ? ` · <a href="mailto:${esc(g[0].correo)}">${esc(g[0].correo)}</a>` : ''}</p>`;
      return (
        cab +
        tablaDe(
          ['Licencia', 'Edición', 'SC', 'Tipo', 'Vence', 'Días', 'Renovación'],
          g.map((l) => [esc(l.clave), esc(l.edicion), l.llamadas ? `<b>${l.llamadas}</b>` : '—', l.perpetua ? 'Mantenimiento' : 'Anual', esc(fechaCorta(l.vence, 'ES')), `<b style="color:${colorDias(l.dias)}">${l.dias}</b>`, l.precio ? `<b>${esc(l.precio)}</b>` : '—']),
        )
      );
    })
    .join('');
}

// Texto que escribe la persona (con {{variables}}, **negrita** y saltos de linea) -> HTML seguro. La variable
// {{licencias.tabla}} se reemplaza por la tabla HTML; la firma del buzon va al final.
export function cuerpoHtml(texto: string, datos: Record<string, string>, tablaHtmlLista: string, firma?: string | null) {
  const parrafo = (t: string) =>
    t
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => `<p style="margin:0 0 12px">${conEnlaces(esc(rellenar(p, datos))).replace(/\*\*(.+?)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br>')}</p>`)
      .join('');
  const partes = texto.split(/\{\{\s*licencias\.tabla\s*\}\}/);
  const cuerpo = partes.map(parrafo).join(tablaHtmlLista);
  const f = (firma ?? '')
    .split('\n')
    .filter((l) => !/^\s*(\{\{\s*agente\.[a-zA-Z]+\s*\}\}\s*)+$/.test(l))
    .join('\n')
    .replace(/\{\{\s*agente\.[a-zA-Z]+\s*\}\}/g, '')
    .trim();
  const htmlFirma = f
    ? `<p style="margin:16px 0 0">${f
        .split('\n')
        .map((l, i) => (i === 0 ? `<b>${conEnlaces(esc(l))}</b>` : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(l.trim()) ? `<a href="mailto:${esc(l.trim())}">${esc(l.trim())}</a>` : conEnlaces(esc(l))))
        .join('<br>')}</p>`
    : '';
  return `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#1d2433;line-height:1.45">${cuerpo}${htmlFirma}</div>`;
}

// Datos de ejemplo para la vista previa en Automatizaciones.
const en = (dias: number) => new Date(Date.now() + dias * 86_400_000).toISOString();
export const EJEMPLO_LICENCIAS: (Licencia & { dias: number })[] = [
  { clave: 'ABCD-1234-EFGH-5678', vence: en(12), edicion: 'Professional', perpetua: false, llamadas: 8, extensiones: null, tipo: 'Commercial', resellerId: 1, dias: 12 },
  { clave: 'WXYZ-9876-QRST-5432', vence: en(25), edicion: 'Enterprise', perpetua: true, llamadas: 16, extensiones: null, tipo: 'Commercial', resellerId: 1, dias: 25 },
];
// Aviso interno: licencias a las que les faltan justo los dias elegidos (15 por defecto).
export const ejemploInterno = (dias: number): LineaInterna[] => [
  { ...EJEMPLO_LICENCIAS[0], vence: en(dias), dias, partner: 'Telecom Andina SAS', correo: 'licencias@telecomandina.co', contacto: 'Ana Pérez', precio: 'USD 430' },
  { ...EJEMPLO_LICENCIAS[1], vence: en(dias), dias, partner: 'Redes del Caribe', correo: 'ops@redescaribe.com', contacto: 'Luis Díaz', precio: 'USD 1.290' },
];
